import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingOverlay from '../components/LoadingOverlay';
import { getAuthHeaders, getToken } from '../utils/auth';
import { apiFetch } from '../utils/api';
import './MultiPickingPage.css';
import { compareItems } from '../utils/itemSort';
import { getOrderAssignment } from '../utils/orderMetrics';

const PLACEHOLDER_IMAGE = 'https://via.placeholder.com/80';
const PRODUCT_META_CACHE_KEY = 'productMetaBySku';
const PRODUCT_META_CACHE_TTL = 4 * 60 * 60 * 1000;
const PRODUCT_META_CACHE_LIMIT = 500;
const productMetaRequests = new Map();

const normalizeUser = (value) => {
  if (!value) return '';
  return String(value).trim().toLowerCase();
};

const emptyProductMeta = () => ({
  image: PLACEHOLDER_IMAGE,
  location: '',
  locationCashCarry: '',
  zone: '',
  lane: '',
  barcode: '',
  hasImage: false,
});

const readProductMetaCache = () => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(PRODUCT_META_CACHE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    const now = Date.now();
    const fresh = {};
    Object.entries(parsed || {}).forEach(([sku, entry]) => {
      if (entry?.savedAt && now - entry.savedAt < PRODUCT_META_CACHE_TTL && entry.meta) {
        fresh[sku] = entry;
      }
    });
    return fresh;
  } catch {
    return {};
  }
};

const writeProductMetaCache = (cache) => {
  if (typeof window === 'undefined') return;
  try {
    const entries = Object.entries(cache || {})
      .sort((a, b) => Number(b[1]?.savedAt || 0) - Number(a[1]?.savedAt || 0))
      .slice(0, PRODUCT_META_CACHE_LIMIT);
    window.localStorage.setItem(PRODUCT_META_CACHE_KEY, JSON.stringify(Object.fromEntries(entries)));
  } catch (err) {
    console.warn('Failed to persist product meta cache:', err);
  }
};

const getCachedProductMeta = (sku) => {
  const key = String(sku || '').trim();
  if (!key) return null;
  return readProductMetaCache()[key]?.meta || null;
};

const setCachedProductMeta = (sku, meta) => {
  const key = String(sku || '').trim();
  if (!key || !meta) return;
  const cache = readProductMetaCache();
  cache[key] = { savedAt: Date.now(), meta };
  writeProductMetaCache(cache);
};

const normStatus = (status) => {
  const s = typeof status === 'string' ? status.toLowerCase() : status;
  return s;
};

const isFoundStatus = (status) => {
  const s = normStatus(status);
  return s === 'found' || s === true || s === 'f' || s === 'ok';
};

const isRemovedStatus = (status) => {
  const s = normStatus(status);
  return s === 'removed' || s === 'remove' || s === false || s === 'rm';
};

const isHoldStatus = (status) => {
  const s = normStatus(status);
  return s === 'hold' || s === 'on_hold';
};

const toKey = (value) => {
  if (value === undefined || value === null) return undefined;
  return String(value);
};

const normalizeEditEntry = (entry, maxQty) => {
  const safeMax = Number.isFinite(maxQty) && maxQty > 0 ? maxQty : 0;
  const clamp = (val) => {
    const num = Number(val);
    if (!Number.isFinite(num)) return 0;
    if (num < 0) return 0;
    if (num > safeMax) return safeMax;
    return num;
  };

  if (entry && typeof entry === 'object') {
    if (entry.removedQty !== undefined || entry.removed !== undefined || entry.diff !== undefined) {
      const removed = clamp(entry.removedQty ?? entry.removed ?? entry.diff);
      return { keepQty: Math.max(safeMax - removed, 0), removedQty: removed };
    }
    if (entry.keepQty !== undefined || entry.keep !== undefined || entry.qty !== undefined) {
      const keep = clamp(entry.keepQty ?? entry.keep ?? entry.qty);
      return { keepQty: keep, removedQty: Math.max(safeMax - keep, 0) };
    }
  }

  if (entry !== undefined) {
    const keep = clamp(entry);
    return { keepQty: keep, removedQty: Math.max(safeMax - keep, 0) };
  }

  return null;
};

const sortAggregatedItems = (items = []) => {
  const decorated = items.map((item) => ({
    item,
    proxy: {
      name: item.name,
      extension_attributes: { lane: item.lane, zone: item.zone },
    },
  }));
  decorated.sort((a, b) => compareItems(a.proxy, b.proxy));
  return decorated.map((entry) => entry.item);
};

const MultiPickingPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [pickedSelections, setPickedSelections] = useState({});
  const [editedQtyByOrder, setEditedQtyByOrder] = useState({});

  const navigate = useNavigate();
  const storedUsername = localStorage.getItem('operator') || localStorage.getItem('username') || '';
  const usernameKey = normalizeUser(storedUsername);

  useEffect(() => {
    try {
      const savedPicked = localStorage.getItem('pickedSelections');
      if (savedPicked) setPickedSelections(JSON.parse(savedPicked));
    } catch (e) {
      console.warn('Failed to parse pickedSelections:', e);
    }
    try {
      const savedEdited = localStorage.getItem('editedQtyByOrder');
      if (savedEdited) setEditedQtyByOrder(JSON.parse(savedEdited));
    } catch (e) {
      console.warn('Failed to parse editedQtyByOrder:', e);
    }
  }, []);

  useEffect(() => {
    const extractBaseSku = (value) => {
      if (!value) return '';
      const str = String(value).trim();
      const idx = str.indexOf('-');
      if (idx <= 0) return str;
      const suffix = str.slice(idx + 1);
      if (/^\d+$/.test(suffix)) {
        // Sufixo numérico: mantém SKU completo
        return str;
      }
      return str.slice(0, idx);
    };

    const getSkuLookupCandidates = (value) => {
      if (!value) return [];
      const sku = String(value).trim();
      if (!sku) return [];
      const idx = sku.indexOf('-');
      if (idx <= 0) return [sku];
      const suffix = sku.slice(idx + 1);
      const baseSku = sku.slice(0, idx);
      if (/^c$/i.test(suffix)) return [sku, baseSku];
      const baseCandidate = extractBaseSku(sku);
      return baseCandidate && baseCandidate !== sku ? [baseCandidate, sku] : [sku];
    };

    const toCatalogUrl = (filePath) => {
      if (!filePath) return null;
      const raw = String(filePath).trim();
      if (!raw) return null;
      if (/^https?:\/\//i.test(raw)) return raw;
      const normalized = raw.startsWith('/') ? raw : `/${raw}`;
      return `/media/catalog/product${normalized}`;
    };

    const pickPrimaryMediaEntry = (entries) => {
      if (!Array.isArray(entries)) return null;
      const preferredTypes = new Set(['image', 'small_image', 'thumbnail']);
      const prioritized = entries.find((entry) => {
        const types = Array.isArray(entry?.types) ? entry.types : [];
        return types.some((type) => preferredTypes.has(String(type).toLowerCase()));
      });
      return prioritized || entries[0] || null;
    };

    const fetchProductMeta = async (sku, fallbackSku) => {
      const candidates = getSkuLookupCandidates(sku);
      if (fallbackSku && !candidates.includes(fallbackSku)) candidates.push(fallbackSku);
      const requestKey = candidates.join('|');
      if (productMetaRequests.has(requestKey)) return productMetaRequests.get(requestKey);

      const request = (async () => {
      try {
        let fallbackMeta = null;

        for (const candidate of candidates) {
          try {
            const cached = getCachedProductMeta(candidate);
            if (cached) {
              if (cached.hasImage) return cached;
              if (!fallbackMeta) fallbackMeta = cached;
              continue;
            }

            const response = await apiFetch(`/rest/V1/products/${encodeURIComponent(candidate)}`, {
              headers: getAuthHeaders(),
            });
            if (!response.ok) continue;
            const data = await response.json();
            const galleryEntry = pickPrimaryMediaEntry(data?.media_gallery_entries);

            const customAttrs = Array.isArray(data?.custom_attributes) ? data.custom_attributes : [];
            const attrMap = customAttrs.reduce((acc, attr) => {
              if (attr?.attribute_code) acc[attr.attribute_code] = attr.value;
              return acc;
            }, {});

            const attrImagePath = attrMap.image || attrMap.small_image || attrMap.thumbnail;
            const resolvedImage = toCatalogUrl(attrImagePath || galleryEntry?.file);
            const image = resolvedImage || PLACEHOLDER_IMAGE;

            const meta = {
              image,
              location: attrMap.location || '',
              locationCashCarry: attrMap.location_cashandcarry || '',
              zone: attrMap.zone || '',
              lane: attrMap.lane || '',
              barcode: data?.barcode || attrMap.barcode || '',
              hasImage: Boolean(resolvedImage),
            };
            setCachedProductMeta(candidate, meta);
            if (resolvedImage) return meta;
            if (!fallbackMeta) fallbackMeta = meta;
          } catch (inner) {
            // try next candidate
          }
        }

        return fallbackMeta || emptyProductMeta();
      } catch (error) {
        console.warn('Product meta lookup failed for', sku, error);
        return emptyProductMeta();
      }
      })();

      productMetaRequests.set(requestKey, request);
      try {
        return await request;
      } finally {
        productMetaRequests.delete(requestKey);
      }
    };

    let cancelled = false;

    const applyProductMetaToItem = (item, meta = emptyProductMeta()) => ({
      ...item,
      image_url: meta.image || item.image_url || PLACEHOLDER_IMAGE,
      extension_attributes: {
        ...item.extension_attributes,
        location: item.extension_attributes?.location || meta.location || '',
        location_cashandcarry: item.extension_attributes?.location_cashandcarry || meta.locationCashCarry || '',
        zone: item.extension_attributes?.zone || meta.zone || '',
        lane: item.extension_attributes?.lane || meta.lane || '',
        barcode: item.extension_attributes?.barcode || meta.barcode || '',
      },
    });

    const enrichOrdersWithProductMeta = async (ordersToEnrich) => {
      const enriched = await Promise.all((ordersToEnrich || []).map(async (detail) => {
        const itemsById = (detail.items || []).reduce((acc, it) => {
          acc[it.item_id] = it;
          return acc;
        }, {});

        const itemsWithMeta = await Promise.all((detail.items || []).map(async (item) => {
          const parent = item.parent_item_id ? itemsById[item.parent_item_id] : null;
          const parentSku = parent?.sku;
          const meta = await fetchProductMeta(item.sku, parentSku);
          return applyProductMetaToItem(item, meta);
        }));

        return { ...detail, items: itemsWithMeta };
      }));

      if (!cancelled) setOrders(enriched);
    };

    const fetchOrders = async () => {
      try {
        await getToken();
        const response = await apiFetch(
          `/rest/V1/orders?searchCriteria[filter_groups][0][filters][0][field]=status&searchCriteria[filter_groups][0][filters][0][value]=picking&searchCriteria[filter_groups][0][filters][0][condition_type]=eq`,
          {
            method: 'GET',
            headers: getAuthHeaders(),
          }
        );
        const data = await response.json();

        const detailed = await Promise.all((data.items || []).map(async (order) => {
          const detailRes = await apiFetch(`/rest/V1/orders/${order.entity_id}`, {
            headers: getAuthHeaders(),
          });
          const detail = await detailRes.json();

          const assignedTo = getOrderAssignment(detail).currentOperator;
          const assignedKey = normalizeUser(assignedTo);
          if (assignedKey && usernameKey && assignedKey !== usernameKey) {
            return null;
          }

          const items = (detail.items || []).map((item) => ({
            ...item,
            image_url: item.image_url || PLACEHOLDER_IMAGE,
          }));

          return { ...detail, items };
        }));

        if (cancelled) return;
        const filtered = detailed.filter(Boolean);
        setOrders(filtered);
        setLoading(false);
        enrichOrdersWithProductMeta(filtered).catch((err) => {
          console.warn('Multi picking product metadata enrichment failed:', err);
        });
      } catch (e) {
        console.error('Failed to load multi picking orders:', e);
        if (!cancelled) {
          setError(e.message || 'Failed to load orders');
          setLoading(false);
        }
      }
    };

    fetchOrders();
    return () => {
      cancelled = true;
    };
  }, [usernameKey]);

  const pickedMapForOrder = (order, store) => {
    if (!order) return {};
    const candidates = [
      String(order.increment_id),
      String(order.entity_id),
    ].filter(Boolean);
    for (const key of candidates) {
      if (store[key]) return store[key] || {};
    }
    return {};
  };

  const aggregatedItems = useMemo(() => {
    if (!orders.length) return [];

    const result = new Map();

    orders.forEach((order) => {
      const pickedMap = pickedMapForOrder(order, pickedSelections);
      const editedMap = pickedMapForOrder(order, editedQtyByOrder);

      (order.items || []).forEach((item) => {
        const itemKey = toKey(item.item_id);
        const statusEntry = itemKey ? pickedMap[itemKey] : undefined;
        const status = typeof statusEntry === 'string' ? statusEntry : statusEntry?.status;
        const removed = isRemovedStatus(status);
        const found = isFoundStatus(status);
        const hold = isHoldStatus(status);

        const rawMax = Number(item.qty_ordered);
        const maxQty = Number.isFinite(rawMax) && rawMax > 0 ? rawMax : 0;
        const editEntry = normalizeEditEntry(editedMap[itemKey], maxQty) ||
          (item.parent_item_id ? normalizeEditEntry(editedMap[toKey(item.parent_item_id)], maxQty) : null);
        const finalQty = editEntry ? editEntry.keepQty : maxQty;
        const removedQty = editEntry ? editEntry.removedQty : 0;
        if (finalQty <= 0 && !removed) return;

        const skuKey = item.sku || `item-${item.item_id}`;
        const existing = result.get(skuKey) || {
          sku: item.sku,
          name: item.name,
          image: item.image_url,
          entries: [],
          totalFound: 0,
          totalPending: 0,
          totalRemoved: 0,
          totalHold: 0,
          lane: item.extension_attributes?.lane || '',
          zone: item.extension_attributes?.zone || '',
        };

        if (!existing.lane && item.extension_attributes?.lane) existing.lane = item.extension_attributes.lane;
        if (!existing.zone && item.extension_attributes?.zone) existing.zone = item.extension_attributes.zone;

        const edited = !removed && !hold && removedQty > 0;

        existing.entries.push({
          orderId: order.entity_id,
          incrementId: order.increment_id,
          qty: finalQty,
          status: removed ? 'removed' : found ? 'found' : hold ? 'hold' : 'pending',
          aisle: item.extension_attributes?.warehouse_location || '',
          location: item.extension_attributes?.location || '',
          locationCash: item.extension_attributes?.location_cashandcarry || '',
          barcode: item.extension_attributes?.barcode || '',
          removedQty,
          edited,
        });

        if (removed) {
          existing.totalRemoved += maxQty;
        } else if (found) {
          existing.totalFound += finalQty;
          if (removedQty > 0) existing.totalRemoved += removedQty;
        } else if (hold) {
          existing.totalHold += finalQty;
        } else {
          existing.totalPending += finalQty;
        }

        result.set(skuKey, existing);
      });
    });

    const aggregated = Array.from(result.values());
    return sortAggregatedItems(aggregated);
  }, [orders, pickedSelections, editedQtyByOrder]);

  return (
    <div className="multi-page">
      <div className="multi-shell">
        <header className="multi-header">
          <div className="multi-actions">
            <button
              className="multi-back"
              onClick={() => {
                const first = orders[0];
                if (first?.increment_id) {
                  navigate(`/picking/${first.increment_id}`);
                } else {
                  navigate('/order-list');
                }
              }}
            >
              ← Back to Picking
            </button>
            <button className="multi-back alt" onClick={() => navigate('/order-list')}>
              Order List
            </button>
          </div>
          <div className="multi-headline">
            <h1>Multi-Order Picking</h1>
            <p>All items currently assigned to you across {orders.length} order{orders.length === 1 ? '' : 's'}.</p>
          </div>
        </header>

        <LoadingOverlay show={loading} ariaLabel="Loading picking items" />
        {error && !loading && <div className="multi-state multi-state--error">{error}</div>}

        {!loading && !error && aggregatedItems.length === 0 && (
          <div className="multi-state multi-state--empty">
            <h3>No items in picking</h3>
            <p>Select orders to begin picking, then return here to see the consolidated list.</p>
          </div>
        )}

        {!loading && !error && aggregatedItems.length > 0 && (
          <div className="multi-grid">
            {aggregatedItems.map((item) => (
              <section key={item.sku || item.name} className="multi-card">
                <div className="multi-card-header">
                  <img src={item.image || 'https://via.placeholder.com/80'} alt={item.name} />
                  <div className="multi-card-body">
                    <div className="multi-card-info">
                      <h3>{item.name}</h3>
                      <div className="multi-tagline">
                        {item.sku && <span className="multi-tag">SKU {item.sku}</span>}
                        <span className="multi-tag found">Found {item.totalFound}</span>
                        <span className="multi-tag pending">Pending {item.totalPending}</span>
                        {item.totalHold > 0 && <span className="multi-tag hold">On Hold {item.totalHold}</span>}
                        {item.totalRemoved > 0 && <span className="multi-tag removed">Removed {item.totalRemoved}</span>}
                      </div>
                    </div>
                    <ul className="multi-list">
                      {item.entries.map((entry) => {
                        const statusLabelMap = {
                          removed: 'Removed',
                          found: 'Found',
                          pending: 'Pending',
                          hold: 'On Hold',
                        };
                        const statusLabel = statusLabelMap[entry.status] || entry.status;
                        const entryClasses = [`entry-${entry.status}`];
                        if (entry.edited) entryClasses.push('entry-edited');

                        return (
                          <li key={`${entry.orderId}-${entry.incrementId}-${entry.qty}`} className={entryClasses.join(' ')}>
                            <div className="entry-main">
                              <span className="entry-order">#{entry.incrementId}</span>
                              {(entry.aisle || entry.location) && (
                                <span className="entry-aisle">
                                  Warehouse {entry.location || entry.aisle || 'N/A'}
                                </span>
                              )}
                              {entry.locationCash && (
                                <span className="entry-store">Store {entry.locationCash}</span>
                              )}
                            </div>
                            <div className="entry-meta">
                              {entry.barcode && <span className="entry-barcode">Barcode: {entry.barcode}</span>}
                              <span className="entry-qty">
                                {entry.qty} pcs
                                {entry.removedQty > 0 && ` (removed ${entry.removedQty})`}
                              </span>
                              <span className={`entry-status ${entry.status}`}>{statusLabel}</span>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiPickingPage;
